/*
 * Casa de Lipa — custom features (Polar Creative Group)
 * ---------------------------------------------------------------------------
 *  Dialog      shared side-panel / bottom-sheet behaviour (size chart, notify)
 *  SizeChart   inches ⇄ cm, highlights the selected size
 *  Colorways   swaps sibling products in place (no page reload)
 *  Notify      Notify-me modal → Klaviyo Back in Stock
 *  Fit         "Complete the fit" cart recommendations + one-tap size add
 *  Countdown   coming soon page timer
 *
 *  No dependencies. Loaded with `defer` after theme.js. Settings come from
 *  window.cdlSettings (layout/theme.liquid).
 */
(function () {
  'use strict';

  var S = window.cdlSettings || {};
  var STR = S.strings || {};
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var routes = (window.theme && window.theme.routes) || {};
  var rootUrl = routes.root || '/';

  var store = {
    get: function (k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };

  function sizedImage(url, width) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function idle(fn, timeout) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: timeout || 2500 });
    else setTimeout(fn, 1200);
  }

  /* =========================================================================
     Dialog
     ========================================================================= */
  var Dialog = {
    openers: new WeakMap(),

    open: function (dlg, opener) {
      if (!dlg || dlg.open) return;
      this.openers.set(dlg, opener || document.activeElement);
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
      document.documentElement.classList.add('cdl-scroll-locked');
      // two frames so the closed transform is painted before animating in
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { dlg.classList.add('is-open'); });
      });
      dlg.dispatchEvent(new CustomEvent('cdl:dialog:open', { bubbles: true, detail: { opener: opener } }));
    },

    close: function (dlg) {
      if (!dlg || !dlg.open || dlg.__closing) return;
      dlg.__closing = true;
      var self = this;
      var panel = dlg.querySelector('.cdl-dialog__panel');
      dlg.classList.remove('is-open');
      var finished = false;
      var done = function () {
        if (finished) return;
        finished = true;
        dlg.__closing = false;
        if (panel) panel.style.transform = '';
        if (typeof dlg.close === 'function') dlg.close();
        else dlg.removeAttribute('open');
        if (!document.querySelector('dialog[data-cdl-dialog][open]')) {
          document.documentElement.classList.remove('cdl-scroll-locked');
        }
        var opener = self.openers.get(dlg);
        if (opener && document.contains(opener) && opener.focus) opener.focus({ preventScroll: true });
        dlg.dispatchEvent(new CustomEvent('cdl:dialog:close', { bubbles: true }));
      };
      if (reduceMotion || !panel) return done();
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 520);
    },

    init: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var openBtn = e.target.closest('[data-cdl-dialog-open]');
        if (openBtn) {
          e.preventDefault();
          self.open(document.getElementById(openBtn.getAttribute('data-cdl-dialog-open')), openBtn);
          return;
        }
        var closeBtn = e.target.closest('[data-cdl-dialog-close]');
        if (closeBtn) {
          e.preventDefault();
          self.close(closeBtn.closest('dialog'));
          return;
        }
        // click on the dimmed area (the <dialog> itself, outside the panel)
        if (e.target.matches && e.target.matches('dialog[data-cdl-dialog]')) self.close(e.target);
      });

      // Esc → animated close instead of an instant one
      document.addEventListener('cancel', function (e) {
        if (e.target.matches && e.target.matches('dialog[data-cdl-dialog]')) {
          e.preventDefault();
          self.close(e.target);
        }
      }, true);

      // Swipe the bottom sheet down to close (phones)
      document.addEventListener('touchstart', function (e) {
        var head = e.target.closest('.cdl-dialog__head');
        if (!head || window.innerWidth >= 750) return;
        var dlg = head.closest('dialog');
        var panel = head.closest('.cdl-dialog__panel');
        var startY = e.touches[0].clientY;
        var dy = 0;
        var move = function (ev) {
          dy = Math.max(0, ev.touches[0].clientY - startY);
          panel.style.transition = 'none';
          panel.style.transform = 'translateY(' + dy + 'px)';
        };
        var end = function () {
          panel.style.transition = '';
          head.removeEventListener('touchmove', move);
          head.removeEventListener('touchend', end);
          if (dy > 80) self.close(dlg);
          else panel.style.transform = '';
        };
        head.addEventListener('touchmove', move, { passive: true });
        head.addEventListener('touchend', end);
      }, { passive: true });
    }
  };

  /* =========================================================================
     Size chart
     ========================================================================= */
  var SizeChart = {
    init: function () {
      var self = this;

      // Tees / Pants / Hoodies tabs — one delegated listener in the capture phase, so nothing
      // else on the page (theme scripts, app scripts, swapped markup) can swallow the tap
      var onTab = function (e) {
        var tab = e.target.closest && e.target.closest('[data-cdl-size-tab]');
        if (!tab) return;
        e.preventDefault();
        e.stopPropagation();
        self.selectTab(tab.closest('[data-cdl-size-dialog]'), tab);
      };
      document.addEventListener('click', onTab, true);
      document.addEventListener('keydown', function (e) {
        var tab = e.target.closest && e.target.closest('[data-cdl-size-tab]');
        if (!tab) return;
        var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var tabs = $$('[data-cdl-size-tab]', tab.parentNode);
        var next = tabs[(tabs.indexOf(tab) + dir + tabs.length) % tabs.length];
        self.selectTab(tab.closest('[data-cdl-size-dialog]'), next);
        next.focus();
      }, true);

      document.addEventListener('cdl:dialog:open', function (e) {
        var dlg = e.target;
        if (!dlg.hasAttribute('data-cdl-size-dialog')) return;
        if (!dlg.__cdlUnits) {
          dlg.__cdlUnits = true;
          $$('[data-cdl-unit]', dlg).forEach(function (btn) {
            btn.addEventListener('click', function () {
              self.setUnit(dlg, btn.getAttribute('data-cdl-unit'));
              store.set('cdl-size-unit', btn.getAttribute('data-cdl-unit'));
            });
          });
        }
        self.setUnit(dlg, store.get('cdl-size-unit') || dlg.getAttribute('data-default-unit') || 'in');
        self.highlight(dlg);
      });
    },

    selectTab: function (dlg, tab) {
      if (!dlg || !tab) return;
      $$('[data-cdl-size-tab]', dlg).forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        // look the panel up inside this dialog (ids can repeat if a product section renders twice)
        var panel = dlg.querySelector('[id="' + t.getAttribute('aria-controls') + '"]');
        if (panel) {
          panel.hidden = !on;
          panel.style.display = on ? '' : 'none';
        }
      });
    },

    setUnit: function (dlg, unit) {
      var base = dlg.getAttribute('data-base-unit') || 'in';
      $$('[data-cdl-unit]', dlg).forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-cdl-unit') === unit));
      });
      $$('td[data-measure]', dlg).forEach(function (td) {
        td.textContent = SizeChart.convert(td.getAttribute('data-measure'), base, unit);
      });
    },

    convert: function (text, from, to) {
      if (from === to) return text;
      var factor = from === 'in' ? 2.54 : 1 / 2.54;
      var fractions = { '¼': 0.25, '½': 0.5, '¾': 0.75, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875 };
      // "22", "22.5", "22 1/2", "22½", "28-30"
      return String(text).replace(/(\d+(?:\.\d+)?)(?:\s*(\d)\/(\d)|\s*([¼½¾⅛⅜⅝⅞]))?/g, function (_, n, a, b, uni) {
        var v = parseFloat(n);
        if (a && b) v += parseInt(a, 10) / parseInt(b, 10);
        if (uni) v += fractions[uni];
        var out = v * factor;
        out = to === 'cm' ? Math.round(out * 2) / 2 : Math.round(out * 4) / 4;
        return String(out % 1 === 0 ? out.toFixed(0) : out);
      });
    },

    highlight: function (dlg) {
      $$('.is-selected', dlg).forEach(function (el) { el.classList.remove('is-selected'); });
      var section = dlg.closest('[data-section-type]');
      if (!section) return;
      var values = $$('[data-product-variants] input:checked, [data-product-variants] [data-popout-input]', section).map(function (i) {
        return String(i.value).trim().toUpperCase();
      });
      var th = $$('thead th[data-size]', dlg).filter(function (h) { return values.indexOf(h.getAttribute('data-size')) > -1; })[0];
      if (!th || th.getAttribute('data-col') === '0') return;
      var col = th.getAttribute('data-col');
      th.classList.add('is-selected');
      $$('td[data-col="' + col + '"]', dlg).forEach(function (td) { td.classList.add('is-selected'); });
    }
  };

  /* =========================================================================
     Colorways — swap the product section in place
     ========================================================================= */
  var Colorways = {
    cache: new Map(),
    token: 0,
    swapped: false,

    init: function () {
      var self = this;
      if (!$('[data-cdl-colorway]')) return;
      var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;

      document.addEventListener('click', function (e) { self.onClick(e); });
      ['mouseover', 'focusin'].forEach(function (type) {
        document.addEventListener(type, function (e) {
          var a = e.target.closest && e.target.closest('[data-cdl-colorway]');
          if (!a) return;
          self.prefetch(a);
          if (canHover || type === 'focusin') self.previewName(a, true);
        });
      });
      ['mouseout', 'focusout'].forEach(function (type) {
        document.addEventListener(type, function (e) {
          var a = e.target.closest && e.target.closest('[data-cdl-colorway]');
          if (a && !(e.relatedTarget && a.contains(e.relatedTarget))) self.previewName(a, false);
        });
      });
      document.addEventListener('touchstart', function (e) {
        var a = e.target.closest && e.target.closest('[data-cdl-colorway]');
        if (a) self.prefetch(a);
      }, { passive: true });

      window.addEventListener('popstate', function () {
        if (self.swapped) window.location.reload();
      });

      // Warm the cache for the other colors once the page is idle
      var conn = navigator.connection;
      if (!(conn && (conn.saveData || /2g/.test(conn.effectiveType || '')))) {
        idle(function () {
          $$('[data-cdl-colorway]:not(.is-current)').slice(0, 6).forEach(function (a) { self.prefetch(a); });
        });
      }
    },

    sectionFor: function (a) { return a.closest('[data-section-type="product"]'); },

    canSwap: function (a) {
      if (!S.colorwaySwap || S.designMode) return false;
      if (!window.fetch || !window.DOMParser) return false;
      var sec = this.sectionFor(a);
      if (!sec || !sec.getAttribute('data-section-id') || !sec.id) return false;
      // Different product template → different section id: load the page normally
      return a.getAttribute('data-template') === a.getAttribute('data-current-template');
    },

    urlFor: function (a, sec) {
      var u = new URL(a.href, window.location.origin);
      u.searchParams.set('section_id', sec.getAttribute('data-section-id'));
      return u.toString();
    },

    prefetch: function (a) {
      if (!this.canSwap(a) || a.classList.contains('is-current')) return null;
      var self = this;
      var key = this.urlFor(a, this.sectionFor(a));
      if (!this.cache.has(key)) {
        var p = fetch(key, { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        });
        p.catch(function () { self.cache.delete(key); });
        this.cache.set(key, p);
      }
      return this.cache.get(key);
    },

    previewName: function (a, on) {
      var block = a.closest('[data-cdl-colorways]');
      var label = block && $('[data-cdl-colorway-name]', block);
      if (!label) return;
      label.textContent = on ? a.getAttribute('data-color') : label.getAttribute('data-current');
    },

    onClick: function (e) {
      var a = e.target.closest && e.target.closest('[data-cdl-colorway]');
      if (!a) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.classList.contains('is-current')) { e.preventDefault(); return; }
      if (!this.canSwap(a)) return;
      e.preventDefault();
      this.swap(a);
    },

    swap: function (a) {
      var self = this;
      var sec = this.sectionFor(a);
      var href = a.href;
      var token = ++this.token;
      var request = this.prefetch(a);
      if (!request) { window.location.href = href; return; }

      $$('.cdl-colorway.is-loading', sec).forEach(function (el) { el.classList.remove('is-loading'); });
      a.classList.add('is-loading');
      var slow = setTimeout(function () { sec.classList.add('cdl-is-swapping'); }, 120);

      request.then(function (html) {
        if (token !== self.token) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById(sec.id);
        if (!fresh || !fresh.querySelector('[data-form-wrapper]')) throw new Error('Unexpected section markup');

        var run = function () { self.apply(sec, fresh); };
        if (document.startViewTransition && !reduceMotion) {
          return document.startViewTransition(run).updateCallbackDone;
        }
        run();
      }).then(function () {
        if (token !== self.token) return;
        if (window.location.href !== href) {
          window.history.pushState({ cdlColorway: true }, '', href);
          self.swapped = true;
        }
        self.afterSwap(sec);
      }).catch(function (err) {
        if (window.console) console.warn('[cdl] colorway swap fell back to page load', err);
        window.location.href = href;
      }).then(function () {
        clearTimeout(slow);
        a.classList.remove('is-loading');
        sec.classList.remove('cdl-is-swapping');
      });
    },

    apply: function (sec, fresh) {
      // 1. Product JSON first — the theme's <product-form> reads it when it connects
      ['script[data-product-json]', 'script[id^="ModelJSON-"]'].forEach(function (sel) {
        var o = sec.querySelector(sel);
        var n = fresh.querySelector(sel);
        if (o && n) o.textContent = n.textContent;
      });
      sec.setAttribute('data-product-handle', fresh.getAttribute('data-product-handle') || '');

      // 2. Gallery (outerHTML so the theme's custom elements upgrade with their children)
      var oImages = sec.querySelector('product-images');
      var nImages = fresh.querySelector('product-images');
      if (oImages && nImages) oImages.outerHTML = nImages.outerHTML;

      // 3. Form column — keep the wrapper node so the theme's sticky behaviour keeps working
      var oWrap = sec.querySelector('[data-form-wrapper]');
      var nWrap = fresh.querySelector('[data-form-wrapper]');
      if (oWrap && nWrap) {
        // Klaviyo embeds render once; carry the rendered ones across
        var keep = {};
        $$('[class*="klaviyo-form-"]', oWrap).forEach(function (el) {
          var cls = (el.className.match(/klaviyo-form-[A-Za-z0-9]+/) || [])[0];
          if (cls) keep[cls] = el;
        });
        var sticky = oWrap.classList.contains('is-sticky');
        oWrap.className = nWrap.className;
        if (sticky) oWrap.classList.add('is-sticky');
        oWrap.innerHTML = nWrap.innerHTML;
        Object.keys(keep).forEach(function (cls) {
          var placeholder = oWrap.querySelector('.' + cls);
          if (placeholder) placeholder.replaceWith(keep[cls]);
        });
      }

      // 4. Cart bar (if enabled)
      var oBar = sec.querySelector('.cart-bar');
      var nBar = fresh.querySelector('.cart-bar');
      if (oBar && nBar) oBar.innerHTML = nBar.innerHTML;
    },

    afterSwap: function (sec) {
      var block = $('[data-cdl-colorways]', sec);
      if (block && block.getAttribute('data-cdl-page-title')) {
        var t = document.createElement('textarea');
        t.innerHTML = block.getAttribute('data-cdl-page-title');
        document.title = t.value;
      }

      // Keep the new photos in view on phones
      var gallery = sec.querySelector('product-images');
      if (gallery && window.innerWidth < 750) {
        var r = gallery.getBoundingClientRect();
        var visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
        if (visible < r.height * 0.3) {
          var headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
          window.scrollTo({ top: window.scrollY + r.top - headerH, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }

      // Analytics: Klaviyo "Viewed Product" for the new color
      try {
        var json = sec.querySelector('script[data-product-json]');
        var p = json && JSON.parse(json.textContent);
        if (p && window.klaviyo && typeof window.klaviyo.track === 'function') {
          window.klaviyo.track('Viewed Product', {
            Name: p.title,
            ProductID: p.id,
            URL: window.location.origin + '/products/' + p.handle,
            ImageURL: p.featured_image ? sizedImage(p.featured_image, 800) : '',
            Brand: p.vendor,
            Price: (p.price || 0) / 100,
            CompareAtPrice: (p.compare_at_price || 0) / 100
          });
        }
      } catch (e) { /* tracking is best effort */ }

      document.dispatchEvent(new CustomEvent('cdl:product:swapped', { detail: { section: sec } }));
    }
  };

  /* =========================================================================
     Notify me → Klaviyo Back in Stock
     ========================================================================= */
  var Notify = {
    KLAVIYO_REVISION: '2025-10-15',

    init: function () {
      var self = this;
      this.dlg = document.getElementById('cdl-notify-dialog');
      if (!S.notify || !this.dlg) return;

      this.form = $('[data-cdl-notify-form]', this.dlg);
      this.email = $('[data-cdl-notify-email]', this.dlg);
      this.msg = $('[data-cdl-notify-msg]', this.dlg);
      this.sizes = $('[data-cdl-notify-sizes]', this.dlg);
      this.sizeList = $('[data-cdl-notify-size-list]', this.dlg);
      this.success = $('[data-cdl-notify-success]', this.dlg);
      this.phone = $('[data-cdl-notify-phone]', this.dlg);
      this.smsConsent = $('[data-cdl-notify-sms-consent]', this.dlg);
      this.emailConsent = $('[data-cdl-notify-email-consent]', this.dlg);
      if (this.emailConsent) this.emailConsent.addEventListener('change', function () { self.setMsg(''); });
      this.smsList = (this.dlg.getAttribute('data-klaviyo-sms-list') || '').trim();
      this.channel = 'email';
      this.productCache = {};

      document.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('[data-cdl-notify-open]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        self.openFrom(btn);
      }, true);

      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submit();
      });
      this.email.addEventListener('input', function () {
        self.email.removeAttribute('aria-invalid');
        self.setMsg('');
      });
      this.sizeList.addEventListener('change', function () {
        self.setMsg('');
        self.updateVariantLabel();
      });

      // Email / Text switch (only rendered when SMS is on in Theme settings)
      if (this.phone && this.smsList) {
        $$('[data-cdl-notify-channel]', this.dlg).forEach(function (r) {
          r.addEventListener('change', function () {
            if (!r.checked) return;
            self.setChannel(r.value, true);
            store.set('cdl-notify-channel', r.value);
          });
        });
        this.phone.addEventListener('input', function () {
          self.phone.removeAttribute('aria-invalid');
          self.setMsg('');
        });
        this.phone.addEventListener('blur', function () {
          self.phone.value = self.formatPhone(self.phone.value);
        });
        this.smsConsent.addEventListener('change', function () { self.setMsg(''); });
      }
    },

    setChannel: function (ch, focus) {
      if (ch !== 'sms' || !this.phone || !this.smsList) ch = 'email';
      this.channel = ch;
      $$('[data-cdl-notify-channel]', this.dlg).forEach(function (r) { r.checked = r.value === ch; });
      $$('[data-cdl-notify-field]', this.dlg).forEach(function (f) { f.hidden = f.getAttribute('data-cdl-notify-field') !== ch; });
      $$('[data-cdl-notify-for]', this.dlg).forEach(function (f) { f.hidden = f.getAttribute('data-cdl-notify-for') !== ch; });
      this.setMsg('');
      if (focus) {
        var input = ch === 'sms' ? this.phone : this.email;
        if (input && !input.value) input.focus({ preventScroll: true });
      }
    },

    // Klaviyo needs E.164 (+15551234567). Numbers without a + are treated as US / Canada.
    toE164: function (raw) {
      var s = String(raw || '').trim();
      var digits = s.replace(/\D/g, '');
      if (s.charAt(0) === '+' || s.indexOf('00') === 0) {
        if (s.indexOf('00') === 0) digits = digits.slice(2);
        if (digits.length < 8 || digits.length > 15) return null;
        if (digits.charAt(0) === '1' && !/^1[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;
        return '+' + digits;
      }
      if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
      if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;
      return '+1' + digits;
    },

    formatPhone: function (raw) {
      var s = String(raw || '').trim();
      if (!s || s.charAt(0) === '+') return s;
      var d = s.replace(/\D/g, '');
      if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
      if (d.length !== 10) return s;
      return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
    },

    openFrom: function (btn) {
      var self = this;
      var data = null;
      var selectedId = null;

      if (btn.hasAttribute('data-cdl-notify-product')) {
        try { data = JSON.parse(btn.getAttribute('data-cdl-notify-product')); } catch (e) { data = null; }
      } else {
        var container = btn.closest('[data-section-type], [data-quick-add-modal], dialog');
        var json = container && container.querySelector('script[data-product-json]');
        if (json) {
          try {
            var p = JSON.parse(json.textContent);
            data = this.normalize(p);
          } catch (e) { data = null; }
        }
        var wrap = btn.closest('[data-form-wrapper]') || container;
        var idInput = wrap && (wrap.querySelector('input[name="id"][form]') || wrap.querySelector('input[name="id"]'));
        selectedId = idInput ? parseInt(idInput.value, 10) : null;
      }

      var go = function (d) {
        if (!d) return;
        self.render(d, selectedId);
        Dialog.open(self.dlg, btn);
        setTimeout(function () {
          var field = self.channel === 'sms' && self.phone ? self.phone : self.email;
          var target = self.sizes.hidden || self.sizeList.querySelector('input:checked') ? field : self.sizeList.querySelector('input');
          if (target) target.focus({ preventScroll: true });
        }, 420);
      };

      if (data) return go(data);
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      this.fetchProduct(handle).then(go).catch(function () { /* stay closed */ });
    },

    normalize: function (p) {
      return {
        handle: p.handle,
        title: p.title,
        image: p.featured_image || (p.images && p.images[0]) || '',
        variants: (p.variants || []).map(function (v) {
          return { id: v.id, title: v.title, available: v.available };
        })
      };
    },

    fetchProduct: function (handle) {
      var self = this;
      if (this.productCache[handle]) return Promise.resolve(this.productCache[handle]);
      return fetch(rootUrl + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (p) { self.productCache[handle] = self.normalize(p); return self.productCache[handle]; });
    },

    render: function (d, selectedId) {
      var self = this;
      this.data = d;
      this.form.hidden = false;
      this.success.hidden = true;
      this.form.classList.remove('is-loading');
      this.setMsg('');

      $('[data-cdl-notify-name]', this.dlg).textContent = d.title || '';
      var img = $('[data-cdl-notify-img]', this.dlg);
      if (d.image) {
        img.src = sizedImage(d.image, 240);
        img.alt = d.title || '';
        img.hidden = false;
      } else {
        img.hidden = true;
      }

      var variants = d.variants || [];
      var hasOptions = variants.length > 1 || (variants[0] && variants[0].title !== 'Default Title');
      var soldOut = variants.filter(function (v) { return !v.available; });
      if (!soldOut.length) soldOut = variants;

      this.sizeList.innerHTML = '';
      if (hasOptions) {
        var preselect = soldOut.filter(function (v) { return v.id === selectedId; })[0] || (soldOut.length === 1 ? soldOut[0] : null);
        soldOut.forEach(function (v) {
          var label = document.createElement('label');
          label.className = 'cdl-notify__size';
          var input = document.createElement('input');
          input.type = 'radio';
          input.name = 'cdl-notify-variant';
          input.value = String(v.id);
          input.checked = !!(preselect && preselect.id === v.id);
          var span = document.createElement('span');
          span.textContent = v.title;
          label.appendChild(input);
          label.appendChild(span);
          self.sizeList.appendChild(label);
        });
        this.sizes.hidden = false;
      } else {
        this.sizes.hidden = true;
      }
      this.updateVariantLabel();

      var saved = store.get('cdl-email');
      if (saved && !this.email.value) this.email.value = saved;

      var note = $('[data-cdl-notify-success-note]', this.dlg);
      if (note) note.hidden = true;
      if (this.emailConsent) this.emailConsent.checked = false;
      if (this.phone && this.smsList) {
        var savedPhone = store.get('cdl-phone');
        if (savedPhone && !this.phone.value) this.phone.value = this.formatPhone(savedPhone);
        this.smsConsent.checked = false;
        this.setChannel(store.get('cdl-notify-channel') || this.channel || 'email', false);
      }
    },

    selectedVariant: function () {
      var d = this.data || {};
      if (this.sizes.hidden) return (d.variants || [])[0] || null;
      var checked = this.sizeList.querySelector('input:checked');
      if (!checked) return null;
      var id = parseInt(checked.value, 10);
      return (d.variants || []).filter(function (v) { return v.id === id; })[0] || null;
    },

    updateVariantLabel: function () {
      var v = this.selectedVariant();
      var el = $('[data-cdl-notify-variant]', this.dlg);
      var showSize = v && !this.sizes.hidden;
      el.textContent = showSize ? v.title + ' — ' + (STR.soldOut || 'Sold out') : (STR.soldOut || 'Sold out');
    },

    setMsg: function (text) { this.msg.textContent = text || ''; },

    klaviyoKey: function () {
      var key = (this.dlg.getAttribute('data-klaviyo-key') || '').trim();
      if (key) return key;
      var scripts = document.querySelectorAll('script[src*="klaviyo"]');
      for (var i = 0; i < scripts.length; i++) {
        var src = scripts[i].src || '';
        var m = src.match(/[?&]company_id=([A-Za-z0-9]+)/) || src.match(/onsite\/js\/([A-Za-z0-9]{6})\/klaviyo\.js/);
        if (m) return m[1];
      }
      return '';
    },

    post: function (path, body) {
      var key = this.klaviyoKey();
      if (!key) return Promise.reject(new Error('Missing Klaviyo public key'));
      return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: this.KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (r.ok) return r;
        return r.text().then(function (t) { throw new Error('Klaviyo ' + r.status + ' ' + t); });
      });
    },

    submit: function () {
      var self = this;
      var variant = this.selectedVariant();
      var sms = this.channel === 'sms' && this.phone && this.smsList;

      if (!variant) {
        this.setMsg(STR.notifySelectSize || 'Select a size');
        var first = this.sizeList.querySelector('input');
        if (first) first.focus();
        return;
      }

      var email = '';
      var phone = '';
      if (sms) {
        phone = this.toE164(this.phone.value);
        if (!phone) {
          this.phone.setAttribute('aria-invalid', 'true');
          this.setMsg(STR.notifyPhoneError || 'Enter a valid mobile number');
          this.phone.focus();
          return;
        }
        if (!this.smsConsent.checked) {
          this.setMsg(STR.notifySmsConsentError || 'Tick the box to agree to texts');
          this.smsConsent.focus();
          return;
        }
      } else {
        email = (this.email.value || '').trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          this.email.setAttribute('aria-invalid', 'true');
          this.setMsg(STR.notifyEmailError || 'Enter a valid email address');
          this.email.focus();
          return;
        }
        if (this.emailConsent && !this.emailConsent.checked) {
          this.setMsg(STR.notifyEmailConsentError || 'Tick the box to agree to emails');
          this.emailConsent.focus();
          return;
        }
      }

      this.form.classList.add('is-loading');
      this.setMsg('');

      var who = sms ? { phone_number: phone } : { email: email };
      var bis = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: who } },
            channels: [sms ? 'SMS' : 'EMAIL']
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variant.id } }
          }
        }
      };

      var steps;
      if (sms) {
        // 1) SMS consent (Klaviyo won't text a number that hasn't agreed), 2) the back-in-stock alert
        steps = this.post('subscriptions', {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Back in stock form (text)',
              profile: { data: { type: 'profile', attributes: { phone_number: phone, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } } } }
            },
            relationships: { list: { data: { type: 'list', id: this.smsList } } }
          }
        }).then(function () {
          return self.post('back-in-stock-subscriptions', bis);
        });
      } else {
        steps = this.post('back-in-stock-subscriptions', bis).then(function () {
          // Email marketing: automatic (notice shown under the button) or only when the box is ticked
          var listId = (self.dlg.getAttribute('data-klaviyo-list') || '').trim();
          var mode = self.dlg.getAttribute('data-email-optin') || 'off';
          var consent = $('[data-cdl-notify-consent]', self.dlg);
          var join = listId && (mode === 'auto' || mode === 'required' || (mode === 'checkbox' && consent && consent.checked));
          if (join) {
            return self.post('subscriptions', {
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Back in stock form (email)',
                  profile: { data: { type: 'profile', attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
                },
                relationships: { list: { data: { type: 'list', id: listId } } }
              }
            }).catch(function (err) {
              // The back-in-stock signup already worked, so the shopper still sees success
              if (window.console) console.warn('[cdl] email list signup failed', err);
            });
          }
        });
      }

      steps.then(function () {
        if (sms) store.set('cdl-phone', phone);
        else store.set('cdl-email', email);

        try { if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify(sms ? { phone_number: phone } : { email: email }); } catch (e) { /* ignore */ }

        var text = $('[data-cdl-notify-success-text]', self.dlg);
        var sizeName = self.sizes.hidden ? '' : variant.title;
        var tpl;
        if (sms) {
          tpl = STR.notifySuccessSms || "We'll text you when [size] is back.";
          if (!sizeName) tpl = tpl.replace(/\s*\[size\]\s*/, ' it ').replace(/\s+/g, ' ');
        } else {
          tpl = sizeName ? (STR.notifySuccessSize || "We'll email you when [size] is back.") : (STR.notifySuccessText || "We'll email you the moment it's back.");
        }
        text.textContent = tpl.replace('[size]', sizeName);
        var note = $('[data-cdl-notify-success-note]', self.dlg);
        if (note) note.hidden = !sms;

        self.form.hidden = true;
        self.success.hidden = false;
        self.success.focus({ preventScroll: true });
      }).catch(function (err) {
        if (window.console) console.warn('[cdl] notify me failed', err);
        self.setMsg(STR.notifyGenericError || 'Something went wrong. Please try again.');
      }).then(function () {
        self.form.classList.remove('is-loading');
      });
    }
  };

  /* =========================================================================
     Fit — "Complete the fit" cart recommendations
     ========================================================================= */
  var Fit = {
    tok: 0,
    recCache: {},

    init: function () {
      var self = this;
      var F = S.fit || {};
      this.cfg = F;
      this.roots = $$('[data-cdl-fit]');
      if (!F.enabled || !this.roots.length) return;

      this.roots.forEach(function (r) {
        self.setupArrows(r);
        self.updateArrows(r);
        // never leave the rail invisible if recommendations are slow
        setTimeout(function () { r.removeAttribute('data-loading'); }, 1500);
      });
      this.hasPage = this.roots.some(function (r) { return r.getAttribute('data-context') === 'page'; });
      this.dirty = true;

      document.addEventListener('click', function (e) { self.onAdd(e); });
      document.addEventListener('theme:cart:change', function () {
        self.dirty = true;
        if (!self.hasPage && !self.drawerOpen()) return; // drawer refreshes when it opens
        clearTimeout(self.refreshTimer);
        self.refreshTimer = setTimeout(function () { self.refresh(); }, self.justAdded ? 1100 : 150);
        self.justAdded = false;
      });
      // The drawer rail only loads when the drawer is opened (no extra requests on every page)
      document.addEventListener('theme:cart-drawer:open', function () {
        if (self.dirty) self.refresh();
      }, true);

      if (this.hasPage) this.refresh();
    },

    drawerOpen: function () {
      var d = document.querySelector('cart-drawer');
      return !!(d && (d.classList.contains('is-open') || d.hasAttribute('open')));
    },

    fetchCart: function () {
      return fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); });
    },

    fetchRecs: function (productId, intent) {
      var key = productId + ':' + intent;
      if (this.recCache[key]) return this.recCache[key];
      var base = routes.product_recommendations_url || (rootUrl + 'recommendations/products');
      var url = base + '?product_id=' + productId + '&limit=' + (this.cfg.limit || 8) + '&intent=' + intent + '&section_id=cdl-recommendations';
      var p = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { return Fit.parseCards(html); })
        .catch(function () { return []; });
      this.recCache[key] = p;
      return p;
    },

    parseCards: function (html) {
      if (!html) return [];
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return $$('[data-cdl-fit-card]', doc).map(function (el) {
        return { id: parseInt(el.getAttribute('data-product-id'), 10), html: el.outerHTML };
      });
    },

    fallbackCards: function (root) {
      var tpl = $('template[data-cdl-fit-fallback]', root);
      if (!tpl) return [];
      return $$('[data-cdl-fit-card]', tpl.content).map(function (el) {
        return { id: parseInt(el.getAttribute('data-product-id'), 10), html: el.outerHTML };
      });
    },

    refresh: function () {
      var self = this;
      var token = ++this.tok;
      var limit = this.cfg.limit || 8;
      this.dirty = false;

      this.fetchCart().then(function (cart) {
        var inCart = {};
        var seeds = [];
        (cart.items || []).forEach(function (it) {
          if (!inCart[it.product_id]) seeds.push(it.product_id);
          inCart[it.product_id] = true;
        });
        seeds = seeds.slice(0, 2); // newest items first

        var jobs = [];
        // Collection mode (default): only the hand-picked collection, in its own order
        if (self.cfg.source === 'recommendations') {
          seeds.forEach(function (id) {
            jobs.push(self.fetchRecs(id, 'complementary'));
            jobs.push(self.fetchRecs(id, 'related'));
          });
        }

        return Promise.all(jobs).then(function (lists) {
          if (token !== self.tok) return;
          self.roots.forEach(function (root) {
            var seen = {};
            var cards = [];
            lists.concat([self.fallbackCards(root)]).forEach(function (list) {
              list.forEach(function (c) {
                if (cards.length >= limit || !c.id || seen[c.id] || inCart[c.id]) return;
                seen[c.id] = true;
                cards.push(c);
              });
            });
            self.render(root, cards, cart.item_count || 0);
          });
        });
      }).catch(function () {
        self.roots.forEach(function (root) { root.removeAttribute('data-loading'); });
      });
    },

    render: function (root, cards, count) {
      var title = $('[data-cdl-fit-title]', root);
      if (title && this.cfg.heading) title.textContent = this.cfg.heading;

      if (count === 0) {
        root.hidden = true;
        return;
      }

      var track = $('[data-cdl-fit-track]', root);
      var current = $$('[data-cdl-fit-card]', track).map(function (el) { return el.getAttribute('data-product-id'); }).join(',');
      var next = cards.map(function (c) { return String(c.id); }).join(',');

      if (current !== next) {
        track.innerHTML = cards.map(function (c, i) {
          return c.html.replace('data-cdl-fit-card', 'data-cdl-fit-card style="--i:' + i + '"');
        }).join('');
        track.scrollLeft = 0;
      }
      root.hidden = cards.length === 0;
      root.removeAttribute('data-loading');
      this.updateArrows(root);
    },

    setupArrows: function (root) {
      var self = this;
      var track = $('[data-cdl-fit-track]', root);
      var prev = $('[data-cdl-fit-prev]', root);
      var next = $('[data-cdl-fit-next]', root);
      if (!track || !prev || !next) return;
      var step = function (dir) {
        track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', function () { step(-1); });
      next.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', function () { self.updateArrows(root); }, { passive: true });
      window.addEventListener('resize', function () { self.updateArrows(root); });
    },

    updateArrows: function (root) {
      var track = $('[data-cdl-fit-track]', root);
      var arrows = $('[data-cdl-fit-arrows]', root);
      if (!track || !arrows) return;
      var overflow = track.scrollWidth > track.clientWidth + 4;
      arrows.hidden = !overflow || !window.matchMedia('(hover: hover)').matches;
      $('[data-cdl-fit-prev]', root).disabled = track.scrollLeft < 4;
      $('[data-cdl-fit-next]', root).disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    },

    onAdd: function (e) {
      var self = this;
      var btn = e.target.closest && e.target.closest('[data-cdl-fit-add]');
      if (!btn) return;
      e.preventDefault();
      if (btn.disabled || btn.classList.contains('is-adding')) return;

      var card = btn.closest('[data-cdl-fit-card]');
      var err = card && $('[data-cdl-fit-error]', card);
      if (err) { err.hidden = true; err.textContent = ''; }
      $$('[data-cdl-fit-add]', card).forEach(function (b) { b.classList.remove('is-added'); });
      btn.classList.add('is-adding');
      this.justAdded = true;

      fetch((routes.cart_add_url || rootUrl + 'cart/add') + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute('data-variant-id'), 10), quantity: 1 }] })
      }).then(function (r) {
        return r.json().then(function (json) {
          if (!r.ok || json.status) throw new Error(json.description || json.message || 'Could not add');
          return json;
        });
      }).then(function () {
        btn.classList.remove('is-adding');
        btn.classList.add('is-added');
        self.syncThemeCart();
      }).catch(function (error) {
        btn.classList.remove('is-adding');
        self.justAdded = false;
        if (err) { err.textContent = error.message; err.hidden = false; }
      });
    },

    // Ask the theme to redraw its cart (line items, totals, free-shipping bar, header count)
    syncThemeCart: function () {
      var items = document.querySelector('cart-items');
      if (items && typeof items.getCart === 'function') {
        items.getCart(); // fires theme:cart:change → this rail refreshes too
      } else {
        window.location.reload();
      }
    }
  };

  /* =========================================================================
     Countdown — coming soon page
     ========================================================================= */
  var Countdown = {
    init: function () {
      $$('[data-cdl-countdown]').forEach(function (el) {
        var target = new Date(el.getAttribute('data-cdl-countdown')).getTime();
        if (isNaN(target)) { el.hidden = true; return; }
        var nums = {};
        $$('[data-unit]', el).forEach(function (n) { nums[n.getAttribute('data-unit')] = n; });
        var pad = function (n) { return n < 10 ? '0' + n : String(n); };
        var tick = function () {
          var diff = Math.max(0, target - Date.now());
          if (diff <= 0) {
            el.classList.add('is-done');
            el.textContent = el.getAttribute('data-done-text') || '';
            return;
          }
          var s = Math.floor(diff / 1000);
          if (nums.d) nums.d.textContent = pad(Math.floor(s / 86400));
          if (nums.h) nums.h.textContent = pad(Math.floor((s % 86400) / 3600));
          if (nums.m) nums.m.textContent = pad(Math.floor((s % 3600) / 60));
          if (nums.s) nums.s.textContent = pad(s % 60);
          setTimeout(tick, 1000 - (Date.now() % 1000));
        };
        tick();
      });
    }
  };

  onReady(function () {
    [Dialog, SizeChart, Colorways, Notify, Fit, Countdown].forEach(function (mod) {
      try { mod.init(); } catch (err) { if (window.console) console.error('[cdl]', err); }
    });
  });

  window.cdl = { Dialog: Dialog, Colorways: Colorways, Notify: Notify, Fit: Fit };
})();
